import { useState, useCallback, useEffect, useMemo, useRef, memo } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "../../config/googleMaps";
import { getMapStyles } from "../map/mapStyles";
import {
  MARKER_SIZE,
  VEHICLE_STATUS_COLORS,
  VehicleMarkerGlyph,
  getMarkerShape,
} from "../map/vehicleMarkers";
import { furthestProgressionOf } from "../../features/shipments/types";
import type { StatusId } from "../../Layouts/lite/components/Sidebar/statusMeta";

export interface ClientShipmentEvent {
  id: string;
  event_type: string;
  date: string;
  address: string | null;
  lat: number | null;
  long: number | null;
}

export interface ClientShipmentUnit {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  status: StatusId;
}

export interface ClientShipmentViewProps {
  clientName: string;
  trackingNumber: string;
  originAddress: string | null;
  destinationAddress: string | null;
  events: ClientShipmentEvent[];
  unit: ClientShipmentUnit | null;
  header?: ReactNode;
  /** Bloque extra bajo los eventos (p. ej. los documentos compartidos con el cliente). */
  footer?: ReactNode;
}

const TRANSIT_EVENT_TYPES = ["transit", "intermediate", "warehouse", "customs", "last_mile"];

const MAP_OPTIONS: google.maps.MapOptions = {
  styles: getMapStyles(),
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
};

const ClientUnitMarker = memo(
  ({ unit, map }: { unit: ClientShipmentUnit; map: google.maps.Map | null }) => {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);

    const markerColor = VEHICLE_STATUS_COLORS[unit.status] || VEHICLE_STATUS_COLORS.offline;
    const markerStyle = getMarkerShape(unit.status, null);

    useEffect(() => {
      if (!map) return;

      const div = document.createElement("div");
      div.style.position = "absolute";

      class UnitOverlay extends google.maps.OverlayView {
        private position: google.maps.LatLng;
        private div: HTMLDivElement;

        constructor(position: google.maps.LatLng, div: HTMLDivElement) {
          super();
          this.position = position;
          this.div = div;
        }

        onAdd() {
          const panes = this.getPanes();
          if (panes) {
            panes.overlayMouseTarget.appendChild(this.div);
          }
        }

        draw() {
          const projection = this.getProjection();
          if (!projection) return;

          const pos = projection.fromLatLngToDivPixel(this.position);
          if (pos) {
            this.div.style.transform = "translate(-50%, -50%)";
            this.div.style.left = `${pos.x}px`;
            this.div.style.top = `${pos.y}px`;
          }
        }

        onRemove() {
          if (this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
          }
        }
      }

      const position = new google.maps.LatLng(unit.latitude, unit.longitude);
      const overlay = new UnitOverlay(position, div);
      overlay.setMap(map);
      overlayRef.current = overlay;
      setContainer(div);

      return () => {
        if (overlayRef.current) {
          overlayRef.current.setMap(null);
          overlayRef.current = null;
        }
        setContainer(null);
      };
    }, [map, unit.latitude, unit.longitude]);

    if (!container) return null;

    return createPortal(
      <div
        className="cursor-pointer"
        style={{
          width: `${MARKER_SIZE}px`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 bg-surface/95 text-content border border-line text-[10px] px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap"
          style={{ marginBottom: "2px" }}
        >
          {unit.name}
        </div>
        <div
          style={{
            position: "relative",
            width: `${MARKER_SIZE}px`,
            height: `${MARKER_SIZE}px`,
          }}
        >
          <VehicleMarkerGlyph
            glyph={markerStyle.glyph}
            color={markerColor}
            size={MARKER_SIZE}
            rotation={markerStyle.rotation}
          />
        </div>
      </div>,
      container,
    );
  },
);

export default function ClientShipmentView({
  clientName,
  trackingNumber,
  originAddress,
  destinationAddress,
  events,
  unit,
  header,
  footer,
}: ClientShipmentViewProps) {
  const { t, i18n } = useTranslation("envios");
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    setMapRef(map);
  }, []);

  const ordered = [...events].sort((a, b) => a.date.localeCompare(b.date));
  const lastEvent = ordered.length > 0 ? ordered[ordered.length - 1] : null;
  const lastPlaced = [...ordered]
    .reverse()
    .find((event) => event.lat !== null && event.long !== null);

  const mapCenter = useMemo(() => {
    if (unit) return { lat: unit.latitude, lng: unit.longitude };
    if (lastPlaced) {
      return { lat: lastPlaced.lat as number, lng: lastPlaced.long as number };
    }
    return null;
  }, [unit?.latitude, unit?.longitude, lastPlaced?.lat, lastPlaced?.long]);

  const reachedTypes = new Set(ordered.map((event) => event.event_type));
  const isDelivered = reachedTypes.has("delivered");
  const hasIncident = lastEvent?.event_type === "incident";
  const currentType = hasIncident
    ? "incident"
    : (furthestProgressionOf([...reachedTypes]) ?? lastEvent?.event_type ?? null);

  const steps = [
    { key: "registered", completed: true },
    { key: "pickup", completed: reachedTypes.has("pickup") || isDelivered },
    {
      key: "transit",
      completed:
        TRANSIT_EVENT_TYPES.some((type) => reachedTypes.has(type)) || isDelivered,
    },
    { key: "warehouse", completed: reachedTypes.has("warehouse") || isDelivered },
    { key: "customs", completed: reachedTypes.has("customs") || isDelivered },
    { key: "last_mile", completed: reachedTypes.has("last_mile") || isDelivered },
    { key: "delivered", completed: isDelivered },
  ];

  const formatDate = (value: string) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : new Intl.DateTimeFormat(i18n.language, {
          day: "2-digit",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        }).format(date);
  };

  const recentEvents = [...ordered].reverse();
  const route = [originAddress, destinationAddress].filter(Boolean).join(" → ");

  return (
    <div className="w-full">
      {header}

      <div className="border border-line rounded-t-lg p-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary-500/10 flex items-center justify-center flex-shrink-0">
            <i className="ri-archive-line text-primary-500 text-xl" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-bold text-content truncate">{clientName}</h2>
            <p className="text-xs text-content-muted font-mono">{trackingNumber}</p>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center gap-2 border-x border-line p-4">
        <div>
          <h3
            className={`text-xl font-bold mb-2 ${
              hasIncident ? "text-error" : "text-primary-500"
            }`}
          >
            {currentType
              ? t(`eventTypes.${currentType}`, { defaultValue: currentType })
              : t("detail.awaitingFirstEvent")}
          </h3>
          {route && <p className="text-sm text-content mb-3">{route}</p>}
        </div>
        {lastEvent && (
          <div className="text-right">
            <p className="text-xs text-content-muted">{t("detail.lastUpdate")}</p>
            <p className="text-sm font-semibold text-content">
              {formatDate(lastEvent.date)}
            </p>
          </div>
        )}
      </div>

      {hasIncident && (
        <div className="border-x border-line px-4 pb-4">
          <div className="flex items-start gap-2 rounded-xl border border-error/30 bg-error/10 px-3 py-2 text-xs text-error">
            <i className="ri-alert-line text-base" />
            <span>{t("detail.incidentNotice")}</span>
          </div>
        </div>
      )}

      <div className="border border-line p-6 overflow-x-auto">
        <div className="flex items-end justify-between gap-2 min-w-max px-2">
          {steps.map((step, idx) => (
            <div key={step.key} className="flex flex-col items-center" style={{ minWidth: "80px" }}>
              <div className="relative w-full flex justify-center">
                <div
                  className={`w-6 h-6 z-10 rounded-full flex items-center justify-center font-bold text-xs border-2 transition-colors ${
                    step.completed
                      ? "bg-primary-500 text-on-primary border-primary-500"
                      : "bg-surface text-content-muted border-line"
                  }`}
                >
                  {step.completed ? <i className="ri-check-line" /> : idx + 1}
                </div>
                {idx < steps.length - 1 && (
                  <div
                    className={`absolute top-3.5 h-0.5 ${
                      step.completed ? "bg-primary-500" : "bg-line"
                    }`}
                    style={{
                      left: "50%",
                      width: "calc(100% + 40px)",
                    }}
                  />
                )}
              </div>
              <p className="text-xs font-medium text-center mt-3 text-content whitespace-nowrap">
                {t(`clientSteps.${step.key}`)}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="border-l border-b border-line rounded-bl-lg overflow-hidden h-64 lg:h-80">
          {isLoaded && mapCenter ? (
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={mapCenter}
              zoom={13}
              onLoad={onMapLoad}
              options={MAP_OPTIONS}
            >
              {mapRef && unit && <ClientUnitMarker unit={unit} map={mapRef} />}
            </GoogleMap>
          ) : (
            <div className="relative w-full h-full bg-subtle flex items-center justify-center">
              <div className="text-center text-content-muted">
                {isLoaded ? (
                  <>
                    <i className="ri-map-pin-line text-3xl mb-2 block opacity-30" />
                    <p className="text-sm">{t("detail.noPosition")}</p>
                  </>
                ) : (
                  <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
                )}
              </div>
            </div>
          )}
        </div>

        <div className="border-x border-b border-line rounded-br-lg p-4">
          <h3 className="font-bold text-base text-content mb-4">
            {t("detail.recentEvents")}
          </h3>
          <div className="space-y-4">
            {recentEvents.length > 0 ? (
              recentEvents.map((event, idx) => (
                <div key={event.id} className="border-l-2 border-primary-500/40 pl-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-content">
                        {t(`eventTypes.${event.event_type}`, {
                          defaultValue: event.event_type,
                        })}
                      </p>
                      <p className="text-xs text-content-muted mt-1">
                        {[formatDate(event.date), event.address].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    {idx === 0 && (
                      <span className="px-2 py-1 rounded text-xs font-medium bg-primary-500/10 text-primary-500 flex-shrink-0 whitespace-nowrap">
                        {t("detail.currentEvent")}
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-content-muted">{t("detail.noEvents")}</p>
            )}
          </div>
        </div>
      </div>

      {footer ? <div className="mt-4">{footer}</div> : null}

      <div className="border-t border-line pt-4 mt-4 flex items-center justify-between text-xs text-content-muted">
        <p>{t("layout.headerTitle")}</p>
        {lastEvent && (
          <p>
            {t("detail.lastUpdate")}: {formatDate(lastEvent.date)}
          </p>
        )}
      </div>
    </div>
  );
}
